const BUCKET = 'walllabs-bucket'
const REGION = 'us-east-1'

function getExtension(fileName = '', mimeType = '') {
  const fromName = String(fileName).match(/\.(png|jpe?g|webp|gif|jfif)$/i)?.[1]
  if (fromName) return fromName.toLowerCase() === 'jpeg' ? 'jpg' : fromName.toLowerCase()

  const fromType = String(mimeType).replace('image/', '').toLowerCase()
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'jfif'].includes(fromType)) {
    return fromType === 'jpeg' ? 'jpg' : fromType
  }

  return 'png'
}

function sanitizeNameSegment(name = '') {
  const normalized = String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[\\/:*?"<>|#%{}^~[\]`]/g, '')
  return normalized || 'wallpaper'
}

function encodeKeyForUrl(key) {
  return String(key || '')
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

export function buildS3Key(wallpaperName, file) {
  const base = sanitizeNameSegment(wallpaperName)
  const extension = getExtension(file?.name, file?.type)
  return `wallpaper/${base}.${extension}`
}

export function getPublicS3Url(key) {
  const cleaned = String(key || '').replace(/^\/+/, '')
  return `https://${BUCKET}.s3.${REGION}.amazonaws.com/${encodeKeyForUrl(cleaned)}`
}

function extractS3KeyFromUrl(url) {
  try {
    const parsed = new URL(String(url || '').trim())
    const host = parsed.hostname.toLowerCase()
    if (
      !host.includes('walllabs-bucket.s3.amazonaws.com') &&
      !host.includes('walllabs-bucket.s3.us-east-1.amazonaws.com')
    ) {
      return ''
    }
    return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''))
  } catch {
    return ''
  }
}

/** Rebuild a consistent public S3 URL (fixes mixed hosts / encoding from API or upload). */
export function normalizeS3PublicUrl(url) {
  const raw = String(url || '').trim()
  if (!raw) return ''

  const key = extractS3KeyFromUrl(raw)
  if (key) {
    return getPublicS3Url(key)
  }

  return raw
}

/** Dev: proxy via /s3-image. Production: direct canonical S3 URL. */
export function getDisplayImageUrl(imageUrl) {
  const url = normalizeS3PublicUrl(imageUrl) || String(imageUrl || '').trim()
  if (!url) return ''

  try {
    const parsed = new URL(url)
    if (
      parsed.hostname.includes('walllabs-bucket.s3.amazonaws.com') ||
      parsed.hostname.includes('walllabs-bucket.s3.us-east-1.amazonaws.com')
    ) {
      if (process.env.NODE_ENV === 'development') {
        return `/s3-image${parsed.pathname}${parsed.search}`
      }
    }
  } catch {
    return url
  }

  return url
}

async function uploadViaProxy(file, key) {
  const encodedKey = encodeKeyForUrl(key)
  const proxyUrls = [
    `/upload-s3/${encodedKey}`,
    `/api/upload-s3/${encodedKey}`
  ]

  let lastError = null

  for (const proxyUrl of proxyUrls) {
    try {
      const response = await fetch(proxyUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })

      let data = {}
      try {
        data = await response.json()
      } catch {
        data = {}
      }

      if (response.ok) {
        return data?.url || getPublicS3Url(key)
      }

      lastError = new Error(
        data?.message || `Upload failed (${response.status})`
      )
    } catch (error) {
      lastError = error
    }
  }

  throw (
    lastError ||
    new Error(
      'Upload failed. On live hosting, set AWS_ACCESS_KEY_ID and AWS_SECRET_ACCESS_KEY in Vercel environment variables.'
    )
  )
}

export async function uploadToS3(file, key) {
  if (!file) throw new Error('No file selected')
  if (!key) throw new Error('No S3 key provided')

  return uploadViaProxy(file, key)
}
